import { GearItem, Rarity, restoreSalvaged, salvageMany } from "@highmarch/domain";
import { outclassed, salvagePreview } from "@highmarch/app";
import { RARITY_LABEL } from "../gearUi";
import { toast } from "../toast";
import type { ArmouryCtx } from "./host";
import { holdButton } from "./hold";
import { btn, gainEl, gainWords, h, ico, isNew, newSince, paysFor, refusal } from "./itemBits";
import { offerUndo } from "./itemActions";
import { closeButton, mountPanel, onRefresh, refreshPanels } from "./itemPanels";
import { gearTile } from "./tile";

/**
 * Clear junk (concept §8, mockup4): the outclassed items — ones no owner would ever pick — preselected up to a rarity
 * ceiling, tap a tile to keep it, the gain (gold and, gear v2, Forge Essence) in big green numbers with the raise it
 * pays for, and one hold to salvage them all; then Undo. It never offers a worn, locked, NEW, Epic-or-better or Mythic item (`outclassed` already rules
 * those out; the filter here says so again, so a stale list can't either).
 */

/** The ceiling chips, and the one it opens on. */
export const JUNK_CEILINGS: readonly Rarity[] = [Rarity.Common, Rarity.Uncommon, Rarity.Rare];
export const JUNK_DEFAULT_CEILING = Rarity.Uncommon;
/** A bulk salvage is one deliberate press, like a single Epic. */
export const JUNK_HOLD_MS = 1100;

/** Never junk, whatever a list says. */
export function protectedItem(ctx: ArmouryCtx, it: GearItem): boolean {
  return it.equippedTo !== null || it.locked || it.rarity >= ctx.B.gear.setMinRarity || it.mythicEffect >= 0 || it.rarity === Rarity.Mythic || isNew(ctx, it);
}

/** Outclassed items at or under the ceiling, weakest first. */
export function junkCandidates(ctx: ArmouryCtx, ceiling: Rarity): GearItem[] {
  return outclassed(ctx.profile, { roster: ctx.scope.roster, seenUpTo: newSince(ctx) })
    .filter((it) => it.rarity <= ceiling && !protectedItem(ctx, it))
    .sort((a, b) => a.rarity - b.rarity || a.level - b.level || a.id - b.id);
}

export function openClearJunk(ctx: ArmouryCtx): void {
  const B = ctx.B, profile = ctx.profile;
  const panel = mountPanel(ctx, "ajunk");
  const root = panel.root;
  let ceiling = JUNK_DEFAULT_CEILING;
  const kept = new Set<number>();

  const render = () => {
    const all = junkCandidates(ctx, ceiling);
    const picked = all.filter((it) => !kept.has(it.id));
    const gain = salvagePreview(B, picked);

    const chips = h("div", "ceil", ...JUNK_CEILINGS.map((r) => {
      const n = junkCandidates(ctx, r).length;
      const c = btn(`cchip${r === ceiling ? " on" : ""}`, "≤ ", h("i", `rdot r${r}`), RARITY_LABEL[r], h("small", "", String(n)));
      c.addEventListener("click", () => { ceiling = r; kept.clear(); render(); });
      return c;
    }));

    const grid = h("div", "jgrid");
    for (const it of all) {
      const on = !kept.has(it.id);
      const t = gearTile(ctx, it, { size: "m" });
      const cell = h("div", `jpick${on ? " picked" : " kept"}`, t, on ? h("span", "chk", "✓") : h("span", "keep", "keep"));
      cell.dataset.id = String(it.id);
      cell.addEventListener("click", () => { if (kept.has(it.id)) kept.delete(it.id); else kept.add(it.id); render(); });
      cell.setAttribute("role", "checkbox");
      cell.setAttribute("aria-checked", String(on));
      grid.append(cell);
    }
    // Nothing to clear is good news: a green tick, not the "no gear" art.
    if (all.length === 0) grid.append(h("div", "jnone", h("span", "jok", "✓"), h("p", "", "Nothing to clear")));

    const pays = picked.length > 0 ? paysFor(ctx, gain.essence) : null;
    const summary = h("div", "gain", h("small", "", picked.length > 0 ? `${picked.length} outclassed item${picked.length === 1 ? " gives" : "s give"} you` : "Tap items to pick them"),
      gainEl(gain.value, "again g big", gain.essence), pays !== null ? h("small", "pays", ico("bolt", 11), `that ${pays}`) : null);
    const hold = holdButton([h("span", "disp", `Salvage ${picked.length}`), h("small", "", "hold to confirm")], {
      ms: JUNK_HOLD_MS, className: "abtn gold go", disabled: picked.length === 0, label: `Hold to salvage ${picked.length} items for ${gainWords(gain.value, gain.essence)}`,
      onDone: () => clear(picked.map((it) => it.id)),
      onTap: () => { if (picked.length > 0) toast(ctx.toastParent, "Hold the button to salvage"); },
    });

    root.replaceChildren(
      h("div", "jt", h("b", "disp", "Clear junk"), closeButton(panel.close)),
      chips, grid, h("div", "go", summary, hold.el));
  };

  function clear(ids: readonly number[]): void {
    const snap = salvageMany(profile, ids);
    if (snap.items.length === 0) { toast(ctx.toastParent, "Nothing was salvaged"); render(); return; }
    ctx.changed();
    refreshPanels(ctx, root);
    panel.close();
    const n = snap.items.length;
    offerUndo(ctx, `Salvaged ${n} item${n === 1 ? "" : "s"}: ${gainWords(snap.value, snap.essence)}`, () => {
      const r = restoreSalvaged(profile, snap);
      if (r !== true) { toast(ctx.toastParent, refusal(r)); return; }
      ctx.changed();
      refreshPanels(ctx);
    });
  }

  onRefresh(panel, render);
  render();
}
